/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// (function () {
//   // ----- config -----
//   const BUNDLE_QUERY = '.BundleProduct__Item';
//   const BUNDLE_QUERY_SHADOW = '.BundleProduct__Item';
//   const MAIN_ACTIVE_SELECTOR = '.media-viewer__item.is-current-variant';
//   const MAIN_IMG_PRIMARY_CLASS = '.img_nv_sg'; // your custom class fallback
//   const MAIN_IMG_FALLBACK = '.product-image';
//   const RETRY_MS = 200;
//   const MAX_ATTEMPTS = 80;

//   // ----- helpers -----
//   function getBundleItems() {
//     const out = [];
//     // items in main DOM
//     document.querySelectorAll(BUNDLE_QUERY).forEach(el => out.push(el));
//     // items inside mw-bundle-product shadowRoot (if present)
//     const mw = document.querySelector('mw-bundle-product');
//     if (mw && mw.shadowRoot) {
//       mw.shadowRoot.querySelectorAll(BUNDLE_QUERY_SHADOW).forEach(el => out.push(el));
//     }
//     return Array.from(new Set(out));
//   }

//   function getActiveElements() {
//     const activeLi = document.querySelector(MAIN_ACTIVE_SELECTOR);
//     if (!activeLi) return null;
//     const mainImg = activeLi.querySelector(MAIN_IMG_PRIMARY_CLASS) || activeLi.querySelector(MAIN_IMG_FALLBACK) || activeLi.querySelector('picture img');
//     const zoomImg = activeLi.querySelector('.js-zoom-image');
//     const zoomLink = activeLi.querySelector('.js-zoom-link, a.media--zoom');
//     return { activeLi, mainImg, zoomImg, zoomLink };
//   }

//   let original = null;
//   function saveOriginalSnapshot() {
//     const el = getActiveElements();
//     if (!el || !el.mainImg) return;
//     original = {
//       src: el.mainImg.src,
//       srcset: el.mainImg.srcset || el.mainImg.getAttribute('srcset') || '',
//       zoom: el.zoomImg?.dataset?.src || el.zoomImg?.getAttribute('data-src') || '',
//       href: el.zoomLink?.href || ''
//     };
//     // store on active li so we can restore even if global var lost
//     try { el.activeLi.dataset._original = JSON.stringify(original); } catch(e){}
//   }

//   function restoreFromSnapshot() {
//     const el = getActiveElements();
//     if (!el || !el.mainImg) return;
//     const v = el.activeLi?.dataset?._original;
//     let snapshot = original;
//     if (v) {
//       try { snapshot = JSON.parse(v); } catch(e){}
//     }
//     if (!snapshot) return;
//     el.mainImg.src = snapshot.src;
//     if (snapshot.srcset) el.mainImg.srcset = snapshot.srcset;
//     if (el.zoomImg && snapshot.zoom) { el.zoomImg.dataset.src = snapshot.zoom; el.zoomImg.src = snapshot.zoom; }
//     if (el.zoomLink && snapshot.href) el.zoomLink.href = snapshot.href;
//   }

//   function swapMainTo(url) {
//     const el = getActiveElements();
//     if (!el || !el.mainImg) return;
//     el.mainImg.src = url;
//     // if you want srcset to match, try set also srcset to url
//     try { el.mainImg.srcset = url; } catch(e){}
//     if (el.zoomImg) {
//       el.zoomImg.dataset.src = url;
//       el.zoomImg.src = url;
//     }
//     if (el.zoomLink) el.zoomLink.href = url;
//   }

//   // ----- bind hover on bundle items (safe: handles items in shadowRoot or normal DOM) -----
//   let bound = false;
//   function bindBundleHover() {
//     const items = getBundleItems();
//     if (!items || !items.length) return false;

//     // remove previous handlers marker to avoid duplicate binding
//     items.forEach(item => {
//       if (item.dataset._hoverBound) return;
//       // find image inside bundle item
//       const img = item.querySelector('img');
//       if (!img) return;
//       item.addEventListener('mouseenter', () => {
//         // swap to bundle image src
//         swapMainTo(img.src || img.getAttribute('src'));
//       });
//       item.addEventListener('mouseleave', () => {
//         restoreFromSnapshot();
//       });
//       item.dataset._hoverBound = '1';
//     });

//     bound = true;
//     console.log('[BUNDLE-HOVER] bound to', items.length, 'items');
//     return true;
//   }

//   // ----- observe gallery active change so we always update original when user clicks thumbnail -----
//   function observeGalleryChanges() {
//     const gallery = document.querySelector('#gallery-viewer') || document.querySelector('.media-viewer');
//     if (!gallery) return;

//     const mo = new MutationObserver(muts => {
//       // if active class changed or children updated, re-save original and rebind
//       const changed = muts.some(m => m.type === 'attributes' || m.type === 'childList');
//       if (changed) {
//         // Slight delay to let Shopify finish its internal swap
//         setTimeout(() => {
//           saveOriginalSnapshot();
//           bindBundleHover();
//         }, 50);
//       }
//     });

//     mo.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'src', 'data-src'] });
//     console.log('[GALLERY-OBSERVE] watching gallery for changes');
//   }

//   // ----- observe for mw-bundle-product insertion (app loads late) -----
//   function observeForBundleApp() {
//     const mo = new MutationObserver(() => {
//       if (getBundleItems().length) {
//         bindBundleHover();
//       }
//     });
//     mo.observe(document.body, { childList: true, subtree: true });
//   }

//   // ----- init with retries -----
//   function init() {
//     let attempts = 0;

//     const attempt = setInterval(() => {
//       attempts++;
//       // make sure gallery exists and we have an active main image
//       const el = getActiveElements();
//       if (el && el.mainImg) {
//         clearInterval(attempt);
//         saveOriginalSnapshot();
//         bindBundleHover();
//         observeGalleryChanges();
//         observeForBundleApp();

//         // re-bind on click events too (thumbnail or variant click triggers DOM updates)
//         document.addEventListener('click', () => {
//           setTimeout(() => {
//             saveOriginalSnapshot();
//             bindBundleHover();
//           }, 80);
//         }, {passive: true});

//         console.log('[BUNDLE-HOVER] initialized');
//         return;
//       }
//       if (attempts >= MAX_ATTEMPTS) {
//         clearInterval(attempt);
//         console.warn('[BUNDLE-HOVER] init timed out — gallery not found');
//       }
//     }, RETRY_MS);
//   }

//   // run
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', init);
//   } else {
//     init();
//   }

//   // expose for debugging
//   window.__bundleHoverDebug = {
//     bindBundleHover,
//     saveOriginalSnapshot,
//     restoreFromSnapshot,
//     swapMainTo,
//     getActiveElements,
//     getBundleItems
//   };
// })();

const header = document.querySelector("store-header");

window.addEventListener("scroll", () => {
  if (window.scrollY > 0) {
    header.classList.add("scrolled");
  } else {
    header.classList.remove("scrolled");
  }
});
 

// For Desktop Grouped Images Hover Code
(function () {
  console.log("🐝 bundle-hover gallery helper initializing...");

  // --- UTILITY FUNCTIONS ---
  function fileName(url = "") {
    try { return url.split("/").pop().split("?")[0]; }
    catch (e) { return url; }
  }

  function getThumbButtons() {
    return Array.from(document.querySelectorAll(".media-thumbs__item")).map(li => {
      const btn = li.querySelector("button");
      const img = li.querySelector("img");
      return { el: btn, imgSrc: img?.src || "", mediaId: li.dataset?.mediaId };
    }).filter(o => !!o.el);
  }

  function clickThumbElement(btn) {
    if (!btn) return false;
    try {
      btn.click();
      btn.dispatchEvent(new MouseEvent("pointerdown", { bubbles: true }));
      btn.dispatchEvent(new MouseEvent("pointerup", { bubbles: true }));
      return true;
    } catch (e) { return false; }
  }

  function fallbackSwapVisible(hoverUrl, titleText) {
    const active = document.querySelector(".media-viewer__item.is-current-variant");
    if (!active) return false;

    const picImg = active.querySelector("picture img");
    const zoomImg = active.querySelector(".js-zoom-image");
    const zoomLink = active.querySelector(".js-zoom-link");
    const titleBox = active.querySelector(".cs-img-title");

    if (picImg) {
      picImg.src = hoverUrl;
      picImg.srcset = hoverUrl;
    }
    if (zoomImg) {
      zoomImg.dataset.src = hoverUrl;
      zoomImg.src = hoverUrl;
    }
    if (zoomLink) zoomLink.href = hoverUrl;

    // Only show title if not main image
    if (titleBox) {
      titleBox.textContent = titleText || "";
      titleBox.style.opacity = titleText ? "1" : "0";
    }

    return true;
  }

  let originalData = null;
  function saveOriginalOnce() {
    if (originalData) return;

    const active = document.querySelector(".media-viewer__item.is-current-variant");
    if (!active) return;

    const pic = active.querySelector("picture img");
    const zoom = active.querySelector(".js-zoom-image");
    const link = active.querySelector(".js-zoom-link");
    const titleBox = active.querySelector(".cs-img-title");

    originalData = {
      src: pic?.src || null,
      srcset: pic?.srcset || null,
      zoom: zoom?.dataset?.src || zoom?.src || null,
      href: link?.href || null,
      title: titleBox?.textContent || ""
    };
    console.log("📌 Saved TRUE original:", originalData);
  }

  function restoreOriginalVisible() {
    if (!originalData) return false;

    const active = document.querySelector(".media-viewer__item.is-current-variant");
    if (!active) return false;

    const pic = active.querySelector("picture img");
    const zoom = active.querySelector(".js-zoom-image");
    const link = active.querySelector(".js-zoom-link");
    const titleBox = active.querySelector(".cs-img-title");

    if (pic && originalData.src) {
      pic.src = originalData.src;
      pic.srcset = originalData.srcset || originalData.src;
    }

    if (zoom && originalData.zoom) {
      zoom.dataset.src = originalData.zoom;
      zoom.src = originalData.zoom;
    }

    if (link && originalData.href) link.href = originalData.href;

    if (titleBox) {
      // Only restore title if not first slide
      if (!active.classList.contains("main-slide")) {
        titleBox.textContent = originalData.title;
        titleBox.style.opacity = "1";
      } else {
        titleBox.textContent = "";
        titleBox.style.opacity = "0";
      }
    }

    return true;
  }

  function findMatchingThumbByUrl(url) {
    if (!url) return null;
    const name = fileName(url);
    const thumbs = getThumbButtons();

    let match = thumbs.find(t => fileName(t.imgSrc) === name);
    if (match) return match.el;

    match = thumbs.find(t => t.imgSrc.includes(name));
    if (match) return match.el;

    return null;
  }

  function attachBundleHoverHandlers(shadow) {
    const items = shadow.querySelectorAll(".BundleProduct__Item");
    if (!items.length) return;

    saveOriginalOnce();

    items.forEach((item) => {
      const hoverImgEl = item.querySelector(".BundleProduct__Image img");
      const titleEl = item.querySelector(".BundleProduct__Title, .BundleProduct__Name, h3, h4");

      const hoverUrl = hoverImgEl?.src || "";
      const titleText = titleEl ? titleEl.textContent.trim() : "";

      if (!hoverUrl) return;

      item.addEventListener("mouseenter", () => {
        saveOriginalOnce();

        const matchBtn = findMatchingThumbByUrl(hoverUrl);
        if (matchBtn) clickThumbElement(matchBtn);
        setTimeout(() => fallbackSwapVisible(hoverUrl, titleText), 60);
      });

      item.addEventListener("mouseleave", () => {
        const first = getThumbButtons()[0]?.el;
        if (first) clickThumbElement(first);
        setTimeout(() => restoreOriginalVisible(), 60);
      });

    });
    console.log("✔ bundle-hover handlers attached");
  }

  // WAIT FOR BUNDLE PRODUCT
  let attempts = 0;
  const waitTimer = setInterval(() => {
    attempts++;
    const mw = document.querySelector("mw-bundle-product");
    if (!mw) return;
    const shadow = mw.shadowRoot;
    if (!shadow) return;
    const items = shadow.querySelectorAll(".BundleProduct__Item");
    if (!items.length) return;

    clearInterval(waitTimer);
    attachBundleHoverHandlers(shadow);
  }, 200);

  // --- GALLERY TITLE HANDLING ---
  document.addEventListener("DOMContentLoaded", () => {
    function setupGalleryTitles() {
      const slides = document.querySelectorAll(".media-viewer__item");
      if (!slides.length) return;

      slides.forEach((slide, index) => {
        const img = slide.querySelector("img");
        if (!img) return;
        const title = img.alt?.trim() || "";

        // First slide → NEVER show title
        if (index === 0) {
          slide.dataset.mainSlide = "1";
          slide.classList.add("main-slide");
          const old = slide.querySelector(".cs-static-title");
          if (old) old.remove();
          return;
        }

        // Other slides → show static title
        let box = slide.querySelector(".cs-static-title");
        if (!box) {
          box = document.createElement("div");
          box.className = "cs-static-title";
          box.style.position = "absolute";
          box.style.bottom = "0";
          box.style.left = "0";
          box.style.right = "0";
          box.style.background = "#FBFAF8";
          box.style.padding = "8px 10px";
          box.style.fontSize = "16px";
          box.style.textTransform = "capitalize";
          box.style.fontWeight = "600";
          box.style.color = "#2b3a63";
          box.style.zIndex = "12";
          box.style.borderTop = "1px solid #ccc";
          slide.style.position = "relative";
          slide.appendChild(box);
        }
        box.textContent = title;
      });
    }

    // wait for gallery images to load
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      if (document.querySelector(".media-viewer__item img") || tries > 50) {
        clearInterval(timer);
        setupGalleryTitles();
      }
    }, 200);
  });
})();

// Hide first image hover text
  document.addEventListener("DOMContentLoaded", () => {
  const slides = document.querySelectorAll(".media-viewer__item");

  if (!slides.length) return;

  slides.forEach((slide, index) => {
    const titleBox = slide.querySelector(".cs-static-title, .cs-img-title");

    if (index === 0) {
      // FIRST / MAIN IMAGE → always hide
      slide.classList.add("main-slide");
      if (titleBox) {
        titleBox.textContent = "";
        titleBox.style.opacity = "0";
      }
    } else {
      // OTHER IMAGES → allow title normally
      if (titleBox && titleBox.textContent.trim() !== "") {
        titleBox.style.opacity = "1";
      }
    }
  });
});


 {/* For mobile */}
document.addEventListener("DOMContentLoaded", () => {

  if (!("ontouchstart" in window)) return; // Only run on mobile/touch

  const MAX_WAIT = 50;
  const WAIT_MS = 150;
  let attempts = 0;

  const wait = setInterval(() => {
    const mw = document.querySelector("mw-bundle-product");
    if (!mw || !mw.shadowRoot) {
      attempts++;
      if (attempts > MAX_WAIT) clearInterval(wait);
      return;
    }

    const items = mw.shadowRoot.querySelectorAll(".BundleProduct__Item");
    if (!items.length) return;

    clearInterval(wait);
    initMobileTap(mw.shadowRoot);
  }, WAIT_MS);

  function initMobileTap(shadow) {
    const mainImages = document.querySelectorAll(".media-viewer__item");

    shadow.querySelectorAll(".BundleProduct__Item").forEach(item => {
      const img = item.querySelector(".BundleProduct__Image img");
      const title = item.querySelector(".BundleProduct__Title, .BundleProduct__Name, h3, h4");

      if (!img) return;

      // 👉 USE ALT TEXT FIRST
      const titleText = img.alt?.trim() || title?.textContent.trim() || "";

      item.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();

        mainImages.forEach(mainImage => {
          const mainPic = mainImage.querySelector("picture, .media-viewer__image");
          const mainTitle = mainImage.querySelector(".cs-img-title");

          // Hide default main image
          if (mainPic) mainPic.style.display = "none";

          // Replace with zoom image
          let zoom = mainImage.querySelector(".js-zoom-image");
          if (zoom) {
            zoom.src = img.src;
            zoom.dataset.src = img.src;
            zoom.style.display = "block";
            zoom.classList.remove("no-js-hidden");
          }

          // Show ALT TEXT
          if (mainTitle) {
            mainTitle.textContent = titleText;
            mainTitle.style.opacity = "1";
          }
        });
      });
    });

    // Tap outside → restore original state
    document.addEventListener("click", e => {
      if (!e.target.closest("mw-bundle-product")) {
        mainImages.forEach(mainImage => {
          const mainPic = mainImage.querySelector("picture, .media-viewer__image");
          const zoom = mainImage.querySelector(".js-zoom-image");
          const mainTitle = mainImage.querySelector(".cs-img-title");

          if (mainPic) mainPic.style.display = "block";
          if (zoom) zoom.style.display = "none";
          if (mainTitle) mainTitle.textContent = "";
        });
      }
    });
  }
});